import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { formatDate } from '../../utils/dateFormatter';
import { useLanguage } from '../../hooks/useLanguage';
import { useGetAdminProxiesQuery } from '../../store/api/adminApi';

type ProxyStatusFilter = '' | 'active' | 'expired' | 'cancelled';
type ServerFilter = '' | '1' | '2' | '3' | '4';

export function AdminProxiesPage() {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [searchParams] = useSearchParams();
  const userIdFromUrl = searchParams.get('userId') ?? '';

  const [page, setPage] = useState(1);
  const [userId, setUserId] = useState(userIdFromUrl);
  const [serverId, setServerId] = useState<ServerFilter>('');
  const [status, setStatus] = useState<ProxyStatusFilter>('');
  const [search, setSearch] = useState('');

  const { data, isLoading, error, refetch } = useGetAdminProxiesQuery({
    page,
    limit: 20,
    userId: userId || undefined,
    serverId: serverId ? Number(serverId) as 1 | 2 | 3 | 4 : undefined,
    status: status || undefined,
    search: search || undefined,
    sortBy: 'purchasedAt',
    sortOrder: -1,
  });

  const proxies = data?.data.proxies ?? [];
  const pagination = data?.data.pagination;

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <div className="text-center py-16">
          <p className="text-neutral-medium text-xl font-body animate-pulse">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
        <h2 className="text-2xl font-heading font-bold text-danger mb-4">{t('errors.network')}</h2>
        <button
          onClick={() => void refetch()}
          className="px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transition-all duration-300"
        >
          {t('common.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen animate-slide-up">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-2">
          {t('admin.proxies.title')}
        </h1>
        <p className="text-neutral-medium text-lg font-body">{t('admin.proxies.subtitle')}</p>
      </div>

      <div className="card-glass rounded-lg p-4 mb-6 border border-neutral-dark">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <input
            value={userId}
            onChange={(e) => { setUserId(e.target.value); setPage(1); }}
            placeholder={t('admin.proxies.filters.userId')}
            className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body"
          />
          <select
            value={serverId}
            onChange={(e) => { setServerId(e.target.value as ServerFilter); setPage(1); }}
            className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body"
          >
            <option value="">{t('admin.proxies.filters.allServers')}</option>
            <option value="1">ISP Proxies</option>
            <option value="2">Datacenter Proxies</option>
            <option value="3">Private Residential Proxies</option>
            <option value="4">Rotating Mobile Proxy 🔥</option>
          </select>
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value as ProxyStatusFilter); setPage(1); }}
            className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body"
          >
            <option value="">{t('admin.proxies.filters.allStatuses')}</option>
            <option value="active">{t('proxies.status.active')}</option>
            <option value="expired">{t('proxies.status.expired')}</option>
            <option value="cancelled">{t('proxies.status.cancelled')}</option>
          </select>
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder={t('admin.proxies.filters.search')}
            className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body"
          />
        </div>
      </div>

      <div className="space-y-3">
        {proxies.map((proxy) => (
          <div key={proxy._id} className="card-glass rounded-xl p-4 border border-neutral-dark/50">
            <div className="flex justify-between items-start gap-3">
              <div className="min-w-0">
                <p className="text-neutral-light font-heading font-semibold break-all">{proxy.ipAddress}:{proxy.port}</p>
                <p className="text-xs text-neutral-medium font-body">
                  {proxy.serverId === 1 ? 'ISP Proxies' : proxy.serverId === 2 ? 'Datacenter Proxies' : proxy.serverId === 3 ? 'Private Residential Proxies' : proxy.serverId === 4 ? 'Rotating Mobile Proxy 🔥' : `Server ${proxy.serverId}`} - {proxy.type || '-'} {proxy.location ? `- ${proxy.location}` : ''}
                </p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-brand-accent font-heading font-semibold">
                  {typeof proxy.price === 'number' ? `$${proxy.price.toFixed(2)}` : '-'}
                </p>
                <p className="text-xs text-neutral-medium">{proxy.status || '-'}</p>
              </div>
            </div>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-2 text-xs font-body">
              {proxy.username && (
                <p className="text-neutral-medium">
                  User: <span className="text-neutral-light">{proxy.username}</span>
                </p>
              )}
              {proxy.password && (
                <p className="text-neutral-medium">
                  Pass: <span className="text-neutral-light">{proxy.password}</span>
                </p>
              )}
              {proxy.port_socks && (
                <p className="text-neutral-medium">
                  SOCKS: <span className="text-neutral-light">{proxy.port_socks}</span>
                </p>
              )}
              {proxy.city && (
                <p className="text-neutral-medium">
                  City: <span className="text-neutral-light">{proxy.city}</span>
                </p>
              )}
              {proxy.carrier && (
                <p className="text-neutral-medium">
                  Carrier: <span className="text-neutral-light">{proxy.carrier}</span>
                </p>
              )}
              {proxy.externalServiceId && (
                <p className="text-neutral-medium">
                  Ext ID: <span className="text-neutral-light">{proxy.externalServiceId}</span>
                </p>
              )}
              {proxy.expiresAt && (
                <p className="text-neutral-medium">
                  Expires: <span className="text-neutral-light">{formatDate(proxy.expiresAt, 'PP h:mm a', currentLanguage, 'Asia/Damascus')}</span>
                </p>
              )}
            </div>
            <div className="mt-2 flex items-center justify-between text-xs">
              <Link
                to={`/admin/users/${proxy.userId}`}
                className="text-brand-accent hover:underline font-body"
              >
                {t('admin.proxies.user')}: {String(proxy.userId)}
              </Link>
              <span className="text-neutral-medium font-body">
                {proxy.purchasedAt ? formatDate(proxy.purchasedAt, 'PP h:mm a', currentLanguage, 'Asia/Damascus') : '-'}
              </span>
            </div>
          </div>
        ))}
      </div>

      {pagination && pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 bg-neutral-dark text-neutral-light rounded-lg disabled:opacity-50"
          >
            {t('common.previous')}
          </button>
          <span className="text-neutral-medium font-body px-4">
            {t('common.page')} {page} {t('common.of')} {pagination.pages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
            disabled={page >= pagination.pages}
            className="px-4 py-2 bg-neutral-dark text-neutral-light rounded-lg disabled:opacity-50"
          >
            {t('common.next')}
          </button>
        </div>
      )}
    </div>
  );
}

